import { FC, ReactNode, useCallback, useState } from 'react';
import { alpha, Box, Fade } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { QcsTypography } from '../../../common/basic/QcsTypography';
import { useTranslation } from 'react-i18next';

interface DropOverlayProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  children: ReactNode;
}

export const DropOverlay: FC<DropOverlayProps> = ({ onFiles, disabled, children }) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) setIsDragOver(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (disabled) return;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        onFiles(Array.from(files));
      }
    },
    [disabled, onFiles],
  );

  return (
    <Box onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} sx={{ position: 'relative', borderRadius: 2 }}>
      <Fade in={isDragOver} timeout={200}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'primary.main',
            backgroundColor: (theme) => alpha(theme.palette.background.default, 0.88),
            pointerEvents: 'none',
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <QcsTypography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {t('common.uploadFile')}
          </QcsTypography>
          <QcsTypography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('common.fileMaxSize')}
          </QcsTypography>
        </Box>
      </Fade>

      <Box
        sx={{
          opacity: isDragOver ? 0.25 : 1,
          transition: 'opacity 0.2s',
          pointerEvents: isDragOver ? 'none' : 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
